import * as React from "react";
import axios, { AxiosResponse } from "axios";
import { useDispatch } from "./";
import { SeriesResponse, ZonesResponse, ViewBy, RankDir } from "./types";
import { TimeFrame, CompareTo } from "../types";
import { getTimeFrameCompareRanges } from "../../../components/Reports/Comfort/utils";

interface GetPortfolio {
  siteIds: string[];
  current: {
    from: string;
    to: string;
  };
  compare?: {
    from: string;
    to: string;
  };
}

interface GetZones {
  siteId: string;
  current: {
    from: string;
    to: string;
  };
  compare?: {
    from: string;
    to: string;
  };
}

export function useActions() {
  const dispatch = useDispatch();

  /**
   * Get portfolio data for sites
   */
  const getPortfolio = React.useCallback(async function getPortfolio({
    siteIds,
    current,
    compare,
  }: GetPortfolio) {
    dispatch({ type: "LOAD_PORTFOLIO", activeSitesHash: siteIds.join("") });

    const url = "/v1/metrics/sites/comfort";
    const payload = {
      siteIds,
      current,
      compare,
    };

    try {
      const { data }: AxiosResponse<SeriesResponse> = await axios.post(
        url,
        payload
      );

      dispatch({ type: "LOAD_PORTFOLIO_SUCCESS", data });
    } catch (err) {
      console.error(err);
      dispatch({ type: "LOAD_PORTFOLIO_FAIL" });
    }
  },
  []);

  const getZones = React.useCallback(async function getZones({
    siteId,
    current,
    compare,
  }: GetZones) {
    dispatch({ type: "LOAD_ZONES" });

    const url = `/v1/metrics/sites/${siteId}/comfort/zones`;
    const payload = {
      current,
      compare,
    };

    try {
      const { data }: AxiosResponse<ZonesResponse> = await axios.post(
        url,
        payload
      );

      dispatch({ type: "LOAD_ZONES_SUCCESS", data });
    } catch (err) {
      console.error(err);
      dispatch({ type: "LOAD_ZONES_FAIL" });
    }
  },
  []);

  const resetZones = React.useCallback(function resetZones() {
    dispatch({ type: "RESET_ZONES" });
  }, []);

  const setViewBy = React.useCallback((viewBy: ViewBy) => {
    dispatch({ type: "SET_VIEW_BY", viewBy });
  }, []);

  const setRankDir = React.useCallback((rankDir: RankDir) => {
    dispatch({ type: "SET_RANK_DIR", rankDir });
  }, []);

  const setTimeFrame = React.useCallback(
    (timeFrameOption: TimeFrame, compareToOption: CompareTo) => {
      const { current, compare } = getTimeFrameCompareRanges(
        timeFrameOption,
        compareToOption
      );

      dispatch({
        type: "SET_TIME_FRAME",
        timeFrameOption,
        compareToOption,
        current: { from: current.from, to: current.to },
        compare: { from: compare.from, to: compare.to },
      });
    },
    []
  );

  return {
    getPortfolio,
    getZones,
    resetZones,
    setViewBy,
    setRankDir,
    setTimeFrame,
  };
}
